import { BitkitSelect, BitkitSelectProps } from '@bitrise/bitkit-v2';
import { useMemo, useState } from 'react';

import getTimeZoneOffsetInMin from '@/utils/getTimeZoneOffsetInMin';

interface TimeZoneDropdownProps extends Omit<BitkitSelectProps, 'items' | 'onValueChange' | 'value'> {
  locale: string;
  onTimeZoneChange(timeZoneLocation: string): void;
}

export const formatTimeZoneLabel = (timeZone: string) => {
  const totalMinutes = getTimeZoneOffsetInMin(timeZone, new Date());
  const hours = Math.floor(Math.abs(totalMinutes) / 60)
    .toString()
    .padStart(2, '0');
  const minutes = (Math.abs(totalMinutes) % 60).toString().padStart(2, '0');
  const sign = totalMinutes < 0 ? '-' : '+';

  return `UTC${sign}${hours}:${minutes} ${timeZone}`;
};

// This is needed until all browsers implement the new spelling
const fixKyivTimeZoneName = (timeZones: string[], timeZoneMap: Map<string, string>) => {
  const oldName = 'Europe/Kiev';
  const newName = 'Europe/Kyiv';
  const label = timeZoneMap.get(oldName);
  if (label) {
    timeZoneMap.delete(oldName);
    timeZoneMap.set(newName, label?.replace('Kiev', 'Kyiv'));
    timeZones.splice(timeZones.indexOf(oldName), 1, newName);
  }
};

export const getTimeZoneDropdownData = (): [string[], Map<string, string>] => {
  const timeZones = Intl.supportedValuesOf('timeZone');

  const map = new Map<string, string>();

  timeZones.forEach((timeZone) => {
    map.set(timeZone, formatTimeZoneLabel(timeZone));
  });

  fixKyivTimeZoneName(timeZones, map);

  return [timeZones, map];
};

const TimeZoneDropdown = ({ locale, onTimeZoneChange, ...rest }: TimeZoneDropdownProps) => {
  const [search, setSearch] = useState('');

  const items = useMemo(() => {
    const [timeZones, timeZoneLabelMap] = getTimeZoneDropdownData();

    return [
      { label: 'UTC', value: 'UTC' },
      ...timeZones.map((timeZone) => ({ label: timeZoneLabelMap.get(timeZone) ?? timeZone, value: timeZone })),
    ];
  }, []);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return items;
    }

    return items.filter((item) => item.label.toLowerCase().includes(query) || item.value === locale);
  }, [items, search, locale]);

  return (
    <BitkitSelect
      label="Time zone"
      value={locale}
      {...rest}
      items={visibleItems}
      onSearchChange={setSearch}
      searchValue={search}
      onValueChange={onTimeZoneChange}
    />
  );
};

export default TimeZoneDropdown;
